import type { PixelEventName } from '@xshop/shared';
import { injectScript, type BrowserPixel, type PixelIdentity, type PixelPayload } from './loader';

const SCRIPT_SRC = 'https://connect.facebook.net/en_US/fbevents.js';
const SCRIPT_ID = 'xshop-meta-pixel';

// The queueing stub Meta's own snippet installs, so calls made before the SDK lands are kept.
interface Fbq {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  loaded?: boolean;
  version?: string;
}

type FbqWindow = Window & { fbq?: Fbq; _fbq?: Fbq };

// Installs the fbq stub if no other tag on the page already did, then loads the real SDK.
function ensureFbq(): Fbq {
  const target = window as FbqWindow;
  if (target.fbq) return target.fbq;

  const fbq = ((...args: unknown[]) => {
    if (fbq.callMethod) fbq.callMethod.apply(fbq, args);
    else fbq.queue.push(args);
  }) as Fbq;
  fbq.queue = [];
  fbq.loaded = true;
  fbq.version = '2.0';
  target.fbq = fbq;
  target._fbq ??= fbq;
  injectScript(SCRIPT_SRC, SCRIPT_ID);
  return fbq;
}

/** The events that count products, where Meta reads how many items there are. */
const COUNTED: ReadonlySet<PixelEventName> = new Set(['AddToCart', 'InitiateCheckout', 'Purchase']);

// Lists the products of an event as Meta's contents, and their ids as content_ids.
function metaContents(payload: PixelPayload): Record<string, unknown> {
  const contents = payload.contents ?? (payload.contentId ? [{ id: payload.contentId, quantity: 1 }] : []);
  if (contents.length === 0) return {};
  return {
    content_ids: contents.map((content) => content.id),
    contents: contents.map(({ id, quantity, itemPrice }) => (itemPrice === undefined ? { id, quantity } : { id, quantity, item_price: itemPrice })),
    content_type: 'product'
  };
}

// Builds the custom_data Meta reads: value, currency, the products, their name, the count and the order.
function metaProperties(event: PixelEventName, payload: PixelPayload): Record<string, unknown> {
  const properties: Record<string, unknown> = {
    currency: payload.currency,
    value: payload.value,
    content_name: payload.contentName,
    num_items: COUNTED.has(event) ? payload.numItems : undefined,
    order_id: event === 'Purchase' ? payload.orderNumber : undefined,
    ...metaContents(payload)
  };
  return Object.fromEntries(Object.entries(properties).filter(([, value]) => value !== undefined && value !== ''));
}

// The Meta browser pixel; every track call carries the same eventID the server dispatch uses.
export const metaPixel: BrowserPixel = {
  // Initialises one fbq instance per configured pixel id, telling Meta which browser this is.
  install(ids, identity: PixelIdentity) {
    const fbq = ensureFbq();
    for (const id of ids) fbq('init', id, { external_id: identity.externalId });
  },

  // Fires one standard event against every initialised pixel, deduplicated by eventID.
  track(event, payload) {
    const target = window as FbqWindow;
    target.fbq?.('track', event, metaProperties(event, payload), { eventID: payload.eventId });
  }
};
